/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import { IComponent, IComponentProps, getCatalogId, msdyn365Commerce } from '@msdyn365-commerce/core';
import {
    addOrderTemplateToCartAction,
    AddOrderTemplateToCartInput,
    OrderTemplate,
    validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    IPopupProductsProps,
    ITelemetryContent,
    PopupProducts,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';

//mmx-feature: custom --start
/*
import React, { useState } from 'react';

import {
    ILinesAddedToCartDialogResources,
    LinesAddedToCartDialog
} from '../lines-added-to-cart-dialog/lines-added-to-cart-dialog.component';
*/

import React, { useEffect, useState} from 'react';
import { setPickupLocationForProduct } from '../../../../utilities/utils';
import { OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import { addPickupOrderTemplateToCartAction, AddPickupOrderTemplateToCartInput } from '../../../../actions/add-pickup-order-template-to-cart.action';
import getDeliveryOptionContextAction, { DeliveryOptionContext, GetDeliveryOptionContextInput } from '../../../../actions/get-delivery-option-context.action';
import { ILinesAddedToCartDialogResources, LinesAddedToCartDialog } from '@msdyn365-commerce/components';
//mmx-feature: custom --end

export interface IAddOrderTemplateToCartComponentData {
    orderTemplate: OrderTemplate;
}

export interface IAddOrderTemplateToCartComponentProps extends IComponentProps<IAddOrderTemplateToCartComponentData> {
    className?: string;
    parentElementRef?: React.RefObject<Button>;
    addToCartText: string;
    telemetryContent?: ITelemetryContent;
    dialogStrings: {
        closeWindowButtonText: string;
        addToCartFailureMessage: string;
        addToCartSuccessMessage: string;
        addToCartProcessMessage: string;
        orderTemplateErrorMessage: string;
        linesAddedToCartDialogResources: ILinesAddedToCartDialogResources;
    };
    isMultipleCatalogFeatureEnabledInHq?: boolean;
    catalogId?: number;
    //mmx-feature: --start
    storeSelectorId?: string;
    //mmx-feature: --end
}

export declare type IAddToCartActionFailureReason = 'EMPTYINPUT' | 'MISSINGDIMENSION' | 'OUTOFSTOCK' | 'CARTACTIONFAILED'; // Rename to used method

export interface IAddOrderTemplateToCartComponent extends IComponent<IAddOrderTemplateToCartComponentProps> {
    onClick(): (event: React.MouseEvent<HTMLElement>, props: IAddOrderTemplateToCartComponentProps) => void;
}

const onClick = async (
    _event: React.MouseEvent<HTMLElement>,
    props: IAddOrderTemplateToCartComponentProps,
    setUploadStatus: (uploadStatus: addOrderTemplateToCartStatus) => void,
    openModal: (opened: boolean) => void,
    setLinesAddedToCartDialogOpen: (opened: boolean) => void,
    setAddedLinesCount: (value: number) => void,
    setErrorMessage: (message: string) => void
): Promise<void> => {
    openModal(true);
    setUploadStatus('LOADING');
    const { context, dialogStrings } = props;
    // assuming selected catalogId will be passed to component and if not passed then get from context
    const catalogId = props.catalogId ?? getCatalogId(props.context.actionContext.requestContext);
    validateCatalogId(catalogId);
    const isMultipleCatalogFeatureEnabledInHq = props.isMultipleCatalogFeatureEnabledInHq || false;
    const addOrderTemplateToCartInput = isMultipleCatalogFeatureEnabledInHq
        ? new AddOrderTemplateToCartInput(props.data.orderTemplate.productList.Id, isMultipleCatalogFeatureEnabledInHq)
        : new AddOrderTemplateToCartInput(props.data.orderTemplate.productList.Id);
    try {
        const addedCartLines = await addOrderTemplateToCartAction(addOrderTemplateToCartInput, context.actionContext);
        setAddedLinesCount(addedCartLines.length);
        openModal(false);
        setTimeout(() => {
            setLinesAddedToCartDialogOpen(true);
        }, 500);
        setErrorMessage('');
    } catch (error) {
        setErrorMessage(dialogStrings.orderTemplateErrorMessage);
        setUploadStatus('FAILED');
    }
};

const AddToCartComponentActions = {
    onClick
};

//mmx-feature: pick up --start
const modalCloseButtonReference: React.RefObject<HTMLButtonElement> = React.createRef();


export interface IPickUpAddOrderTemplateToCartComponent extends IComponent<IAddOrderTemplateToCartComponentProps> {
    pickUpOnClick(): (event: React.MouseEvent<HTMLElement>, props: IAddOrderTemplateToCartComponentProps) => void;
}

//mmx-feature internal: diff from onClick --start
/*
const pickUpOnClick = async (
*/
export const pickUpOnClick = async (
    //mmx-feature internal: diff from onClick --end
    _event: React.MouseEvent<HTMLElement>,
    props: IAddOrderTemplateToCartComponentProps,
    setUploadStatus: (uploadStatus: addOrderTemplateToCartStatus) => void,
    openModal: (opened: boolean) => void,
    setLinesAddedToCartDialogOpen: (opened: boolean) => void,
    setAddedLinesCount: (value: number) => void
    ): Promise<void> => {
        openModal(true);
        setUploadStatus('LOADING');

        const product = props.data.orderTemplate.orderTemplateLines[0].fullProduct?.ProductDetails;

        if (product) {
            const onLocationSelected = async (orgUnitLocation: OrgUnitLocation, deliveryMode: string) => {
                openModal(true);
                const { context } = props;
                const locationData = { orgUnitLocation, deliveryMode };
                const addOrderTemplateToCartInput = new AddPickupOrderTemplateToCartInput(props.data.orderTemplate.productList.Id, locationData);
                const addedCartLines = await addPickupOrderTemplateToCartAction(addOrderTemplateToCartInput, context.actionContext);
                setAddedLinesCount(addedCartLines.length);
                openModal(false);
                setTimeout(() => {
                    setLinesAddedToCartDialogOpen(true);
                }, 500);
            }
    
            openModal(false);
            await setPickupLocationForProduct(
                product,
                0,
                onLocationSelected,
                props.storeSelectorId ?? 'mmx-store-selector',
                modalCloseButtonReference, 
                props.context.request.apiSettings.channelId,
                props.context.actionContext,
                props.telemetryContent?.telemetry
            )
        } else {
            openModal(false);
        }
};

const PickUpAddToCartComponentActions = {
    pickUpOnClick
};
//mmx-feature: pick up --end

export type addOrderTemplateToCartStatus = 'LOADING' | 'SUCCESS' | 'FAILED' | 'WAITING';

const _getModalText = (status: addOrderTemplateToCartStatus, props: IAddOrderTemplateToCartComponentProps, errorMsg: string) => {
    const { dialogStrings } = props;
    const { addToCartFailureMessage, addToCartSuccessMessage, addToCartProcessMessage } = dialogStrings;

    if (status === 'LOADING') {
        return `${addToCartProcessMessage}...`;
    }

    if (status === 'SUCCESS') {
        return addToCartSuccessMessage;
    }

    if (status === 'FAILED') {
        return errorMsg !== null ? errorMsg : addToCartFailureMessage;
    }

    return '';
};

let buttonEventReference: HTMLButtonElement | undefined;

const AddOrderTemplate: React.FC<IAddOrderTemplateToCartComponentProps> = (props: IAddOrderTemplateToCartComponentProps) => {
    const [uploadStatus, setUploadStatus] = useState<addOrderTemplateToCartStatus>('WAITING');
    const [modalOpen, setModalOpen] = useState(false);
    const [addedLinesCount, setAddedLinesCount] = useState(0);
    const [linesAddedToCartDialogOpen, setLinesAddedToCartDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    //mmx-feature: dynamic shipping check --start
    const [deliveryOptionContext, updateDeliveryOptionContext] = useState<DeliveryOptionContext>();
    //mmx-feature: dynamic shipping check --end

    const onClickHandler = async (event: React.MouseEvent<HTMLElement>) => {
        buttonEventReference = event.target as HTMLButtonElement;
        await AddToCartComponentActions.onClick(
            event,
            props,
            setUploadStatus,
            setModalOpen,
            setLinesAddedToCartDialogOpen,
            setAddedLinesCount,
            setErrorMessage
        );
    };
    //mmx-feature: pickup option --start
    const pickUpOnClickHandler = async (event: React.MouseEvent<HTMLElement>) => {
        buttonEventReference = event.target as HTMLButtonElement;
        await PickUpAddToCartComponentActions.pickUpOnClick(event, props, setUploadStatus, setModalOpen, setLinesAddedToCartDialogOpen, setAddedLinesCount);
    };
    //mmx-feature: pickup option --end

    const closeLinesAddedToCartDialog = () => {
        setLinesAddedToCartDialogOpen(false);
        setErrorMessage('');
        buttonEventReference?.focus();
    };

    const modalText = _getModalText(uploadStatus, props, errorMessage);
    const processStatusClass = uploadStatus.toLowerCase();
    const dialogStrings = { closeWindowButtonText: props.dialogStrings.closeWindowButtonText };
    const payLoad = getPayloadObject('click', props.telemetryContent!, TelemetryConstant.AddOrderTemplateToCart);
    const addOrderTemplateAttribute = getTelemetryAttributes(props.telemetryContent!, payLoad);

    const popupProps: IPopupProductsProps = {
        dialogStrings,
        className: 'msc-add-order-template-to-cart',
        processStatusClass,
        text: modalText,
        modalOpen,
        setModalOpen
    };

    const renderModalPopup = <PopupProducts {...popupProps} />;

    const products = props.data.orderTemplate.orderTemplateLines
        .map(line => line.fullProduct)
        .filter(fullProduct => fullProduct) as FullProduct[];
    const isMultipleCatalogFeatureEnabledInHq = props.isMultipleCatalogFeatureEnabledInHq || false;

    //mmx-feature: pickup & dynamic shipping check --start
    /*
    return (
        <>
            {renderModalPopup}
            <button
                className={classnames(
                    'msc-add-order-template-to-cart msc-btn',
                    props.className,
                    props.data.orderTemplate.totalLines === 0 ? 'disabled' : ''
                )}
                aria-label={props.addToCartText}
                disabled={props.data.orderTemplate.totalLines === 0}
                type='button'
                onClick={onClickHandler}
                {...addOrderTemplateAttribute}
            >
                {props.addToCartText}
            </button>
            <LinesAddedToCartDialog
                className='msc-lines-added-to-cart-dialog'
                id={props.id}
                typeName={props.typeName}
                context={props.context}
                dialogStrings={props.dialogStrings.linesAddedToCartDialogResources}
                data={{
                    linesCount: addedLinesCount,
                    orderTemplate: props.data.orderTemplate.productList,
                    products
                }}
                modalOpen={linesAddedToCartDialogOpen}
                onClose={closeLinesAddedToCartDialog}
                isMultipleCatalogFeatureEnabledInHq={isMultipleCatalogFeatureEnabledInHq || false}
                {...(isMultipleCatalogFeatureEnabledInHq && { catalogId: props.catalogId })}
            />
        </>
    );
    */
    useEffect(() => {
        const getData = async () => {
            const delivOptionConfig = await getDeliveryOptionContextAction(new GetDeliveryOptionContextInput(props.context.actionContext.requestContext.apiSettings.channelId), props.context.actionContext);;
            updateDeliveryOptionContext(delivOptionConfig);
        }
        getData();
    }, []);

    return (
        <>
            {renderModalPopup}
            {deliveryOptionContext?.isShippingEnabled &&
                <button
                      className={classnames(
                          'msc-add-order-template-to-cart msc-btn',
                          props.className,
                          props.data.orderTemplate.totalLines === 0 ? 'disabled' : ''
                      )}
                      aria-label={props.addToCartText}
                      disabled={props.data.orderTemplate.totalLines === 0}
                      type='button'
                      onClick={onClickHandler}
                      {...addOrderTemplateAttribute}
                  >
                      Ship it
                  </button>
            }
            {deliveryOptionContext?.isPickupEnabled &&
                <button
                    className={classnames('msc-add-order-template-to-cart msc-btn', props.className, props.data.orderTemplate.totalLines === 0 ? 'disabled' : '')}
                    aria-label={props.addToCartText}
                    disabled={props.data.orderTemplate.totalLines === 0}
                    type='button'
                    onClick={pickUpOnClickHandler}
                    {...addOrderTemplateAttribute}
                >
                    Pick up
                </button>
            }
            <LinesAddedToCartDialog
                className='msc-lines-added-to-cart-dialog'
                id={props.id}
                typeName={props.typeName}
                context={props.context}
                dialogStrings={props.dialogStrings.linesAddedToCartDialogResources}
                data={{
                    linesCount: addedLinesCount,
                    orderTemplate: props.data.orderTemplate.productList,
                    products
                }}
                modalOpen={linesAddedToCartDialogOpen}
                onClose={closeLinesAddedToCartDialog}
                isMultipleCatalogFeatureEnabledInHq={isMultipleCatalogFeatureEnabledInHq || false}
                {...(isMultipleCatalogFeatureEnabledInHq && { catalogId: props.catalogId })}
            />
        </>
    );
    //mmx-feature: pickup & dynamic shipping check --end
};

export const AddOrderTemplateToCartComponent: React.FunctionComponent<IAddOrderTemplateToCartComponentProps> =
    // @ts-expect-error -- As per design, custom component
    msdyn365Commerce.createComponentOverride<IAddOrderTemplateToCartComponent>('AddOrderTemplate', {
        component: AddOrderTemplate,
        ...AddToCartComponentActions
    });


export default AddOrderTemplateToCartComponent;